/**
 * Day / week / month grain for the Forecast cash chart.
 *
 * Monthly zoom stays in month-index units. Day and Week reuse that camera
 * (fractional x inside each month slot) so swapping row density does not
 * jump the window. Each grain totals AR / AP / planned / job / burn over
 * that period the same way Month totals a calendar month.
 */

import type { ForecastQuoteCoverageMarker } from './financesQuoteCoverageWarnings';
import type { CashFlowProjectionItem, CashFlowProjectionMonth } from './cashFlowProjection';
import { toMonthKey } from './financesForecast';
import type { DailyFinanceReport } from '../types/finances';
import {
  listPayDatesInRange,
  parsePayrollSchedule,
  payrollAmountPerPayday,
  type PayrollSchedule,
} from './leaveAccrual';

export {
  typicalPaydaysPerMonth,
  payrollAmountPerPayday,
  scaleMonthlyPayrollForPaydays,
} from './leaveAccrual';

export type ForecastChartGrain = 'monthly' | 'weekly' | 'daily';
export type ForecastZoomPreset = 'day' | 'week' | 'month';

export const FORECAST_AVG_DAYS_PER_MONTH = 30.4375;
/** Tightest wheel zoom — about one day in month units. */
export const FORECAST_MIN_ZOOM_MONTHS = 1 / FORECAST_AVG_DAYS_PER_MONTH;
/** Day preset: 90 calendar days of daily bars. */
export const FORECAST_DAY_ZOOM_DAYS = 90;
/** Opening Day view puts today one third of the way across the chart. */
export const FORECAST_DAY_ANCHOR_FRACTION = 1 / 3;
export const FORECAST_DAY_ZOOM_MONTHS = FORECAST_DAY_ZOOM_DAYS / FORECAST_AVG_DAYS_PER_MONTH;
/** Week preset: 12 Sunday–Saturday weeks of weekly stacks. */
export const FORECAST_WEEK_ZOOM_DAYS = 84;
export const FORECAST_WEEK_ZOOM_MONTHS = FORECAST_WEEK_ZOOM_DAYS / FORECAST_AVG_DAYS_PER_MONTH;
/** Show daily bars at the 90-day Day window (and anything tighter). */
export const FORECAST_DAILY_ENTER_MONTHS = FORECAST_DAY_ZOOM_MONTHS;
/** Leave daily a bit wider than 90 days so the Day preset does not flicker. */
export const FORECAST_DAILY_LEAVE_MONTHS = 120 / FORECAST_AVG_DAYS_PER_MONTH;
export const FORECAST_DAILY_PAD_DAYS = 7;

export type ForecastDayBucket = {
  dayKey: string;
  day: Date;
  monthKey: string;
  arIn: number;
  apOut: number;
  plannedOut: number;
  jobOut: number;
  items: CashFlowProjectionItem[];
  quoteMarkers: ForecastQuoteCoverageMarker[];
  hasEstimatedAr: boolean;
};

export type ForecastDailyBalancePoint = {
  dayKey: string;
  day: Date;
  monthKey: string;
  /** End-of-day projected cash after that day's net. */
  projectedCash: number;
  arIn: number;
  apOut: number;
  plannedOut: number;
  jobOut: number;
  burn: number;
  items: CashFlowProjectionItem[];
  quoteMarkers: ForecastQuoteCoverageMarker[];
  hasEstimatedAr: boolean;
};

export type ForecastWeeklyBalancePoint = {
  weekKey: string;
  weekStart: Date;
  weekEnd: Date;
  monthKey: string;
  /** End-of-week projected cash (last day's close in the walk). */
  projectedCash: number;
  arIn: number;
  apOut: number;
  plannedOut: number;
  jobOut: number;
  burn: number;
  items: CashFlowProjectionItem[];
  quoteMarkers: ForecastQuoteCoverageMarker[];
  hasEstimatedAr: boolean;
};

export function toDayKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function parseDayKey(key: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key);
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }
  return date;
}

export function daysInCalendarMonth(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
}

export function startOfLocalDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export function addLocalDays(d: Date, days: number): Date {
  const next = startOfLocalDay(d);
  next.setDate(next.getDate() + days);
  return next;
}

export function enumerateDays(start: Date, end: Date): Date[] {
  const out: Date[] = [];
  const last = startOfLocalDay(end);
  const cur = startOfLocalDay(start);
  while (cur <= last) {
    out.push(new Date(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

/** US weeks: Sunday through Saturday. */
export function startOfWeekSunday(d: Date): Date {
  const day = startOfLocalDay(d);
  return addLocalDays(day, -day.getDay());
}

export function toWeekKey(d: Date): string {
  return toDayKey(startOfWeekSunday(d));
}

export function enumerateWeeks(start: Date, end: Date): Date[] {
  const out: Date[] = [];
  let cur = startOfWeekSunday(start);
  const last = startOfLocalDay(end);
  while (cur <= last) {
    out.push(new Date(cur));
    cur = addLocalDays(cur, 7);
  }
  return out;
}

/** Wednesday of the week — sits the weekly bar in the middle of the slot. */
export function weekPlotDay(weekStart: Date): Date {
  return addLocalDays(startOfWeekSunday(weekStart), 3);
}

export function dayKeysInMonth(month: Date): string[] {
  const start = new Date(month.getFullYear(), month.getMonth(), 1);
  const dim = daysInCalendarMonth(start);
  const keys: string[] = [];
  for (let day = 1; day <= dim; day += 1) {
    keys.push(toDayKey(new Date(start.getFullYear(), start.getMonth(), day)));
  }
  return keys;
}

/**
 * Overdue or undated cash lands on today — same "count it now" rule as the
 * monthly bucket, but at day resolution. Unapproved quote placeholders keep
 * their scheduled (task midpoint) day instead.
 */
export function cashFlowItemDayKey(
  item: Pick<CashFlowProjectionItem, 'dueDate' | 'quotePlaceholder'>,
  today: Date,
): string {
  const todayKey = toDayKey(today);
  if (!item.dueDate) return todayKey;
  const dueKey = toDayKey(item.dueDate);
  if (item.quotePlaceholder) return dueKey;
  return dueKey < todayKey ? todayKey : dueKey;
}

export function quoteMarkerDayKey(
  marker: Pick<ForecastQuoteCoverageMarker, 'taskStart' | 'cashDate'>,
  _today: Date,
): string {
  return toDayKey(marker.cashDate ?? marker.taskStart);
}

/** Fractional x inside the month slot: day 1 → monthIndex, last day < monthIndex + 1. */
export function xForDay(day: Date, monthIndex: number): number {
  const dim = Math.max(1, daysInCalendarMonth(day));
  return monthIndex + (day.getDate() - 1) / dim;
}

export function xForWeek(weekStart: Date, monthIndex: number): number {
  return xForDay(weekPlotDay(weekStart), monthIndex);
}

export function dateFromMonthX(x: number, monthStarts: Date[]): Date | null {
  if (!monthStarts.length) return null;
  const max = monthStarts.length;
  const clamped = Math.min(Math.max(x, 0), max - 1e-9);
  const idx = Math.min(max - 1, Math.max(0, Math.floor(clamped)));
  const start = monthStarts[idx];
  if (!start) return null;
  const dim = daysInCalendarMonth(start);
  const frac = clamped - idx;
  // Inverse of xForDay: (date - 1) / dim. Round so the last day of a 30/31-day
  // month does not slip backward on floating-point (29/30 * 30 < 29).
  const day = Math.min(dim, Math.max(1, Math.round(frac * dim) + 1));
  return new Date(start.getFullYear(), start.getMonth(), day);
}

export function formatForecastDayLabel(day: Date): string {
  return day.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function formatForecastWeekLabel(weekStart: Date): string {
  return `Week of ${formatForecastDayLabel(weekStart)}`;
}

export function visibleMonthSpan(
  zoomWindow: { start: number; end: number } | null,
  nMonths: number,
): number {
  if (!zoomWindow) return nMonths;
  return Math.max(0, (zoomWindow.end - zoomWindow.start) * nMonths);
}

export function clampForecastZoomWindow(
  start: number,
  end: number,
): { start: number; end: number } | null {
  let ns = start;
  let ne = end;
  if (ns < 0) { ne -= ns; ns = 0; }
  if (ne > 1) { ns -= ne - 1; ne = 1; }
  ns = Math.max(0, Math.min(ns, 1));
  ne = Math.max(ns, Math.min(ne, 1));
  if (ns <= 0.0005 && ne >= 0.9995) return null;
  return { start: ns, end: ne };
}

/**
 * Opening camera: the last three months of cash, then the runway ahead.
 * Scroll left for older history. `nowX` is in month-index units (same as `xForDay`).
 */
export function runwayZoomWindow(
  nMonths: number,
  nowX: number,
): { start: number; end: number } | null {
  const n = Math.max(1, nMonths);
  const firstMonth = Math.max(0, Math.floor(nowX) - 2);
  const start = firstMonth / n;
  return clampForecastZoomWindow(start, 1);
}

/**
 * Zoom window of `spanMonths` with `anchorX` at `anchorFraction` across the
 * plot (0 = left edge, 0.5 = center). `anchorX` is in month-index units.
 */
export function zoomWindowAroundAnchor(
  nMonths: number,
  spanMonths: number,
  anchorX: number,
  anchorFraction = 0.5,
): { start: number; end: number } | null {
  const n = Math.max(1, nMonths);
  const span = Math.min(1, Math.max(FORECAST_MIN_ZOOM_MONTHS / n, spanMonths / n));
  if (span >= 0.999) return null;
  const fraction = Math.min(1, Math.max(0, anchorFraction));
  // xDomain = [start * n - 0.5, (start + span) * n - 0.5]
  const start = (anchorX + 0.5) / n - fraction * span;
  return clampForecastZoomWindow(start, start + span);
}

export function forecastZoomPreset(
  zoomWindow: { start: number; end: number } | null,
  nMonths: number,
): ForecastZoomPreset {
  if (!zoomWindow) return 'month';
  const visible = visibleMonthSpan(zoomWindow, nMonths);
  if (visible > FORECAST_DAILY_LEAVE_MONTHS) return 'month';
  // Week is tighter than Day (7 days vs 90). Split at the midpoint.
  const weekDayMid = (FORECAST_DAY_ZOOM_MONTHS + FORECAST_WEEK_ZOOM_MONTHS) / 2;
  if (visible <= weekDayMid) return 'week';
  return 'day';
}

export function forecastChartGrainFromPreset(preset: ForecastZoomPreset): ForecastChartGrain {
  if (preset === 'week') return 'weekly';
  if (preset === 'day') return 'daily';
  return 'monthly';
}

export function forecastChartGrain(
  visibleMonths: number,
  prevGrain: ForecastChartGrain,
): ForecastChartGrain {
  if (!Number.isFinite(visibleMonths) || visibleMonths <= 0) return 'monthly';
  if (prevGrain === 'daily') {
    return visibleMonths > FORECAST_DAILY_LEAVE_MONTHS ? 'monthly' : 'daily';
  }
  return visibleMonths <= FORECAST_DAILY_ENTER_MONTHS ? 'daily' : 'monthly';
}

export function dailyChartBarSize(plotWidth: number, visibleDays: number): number {
  const days = Math.max(1, visibleDays);
  const width = Math.max(1, plotWidth);
  return Math.max(3, Math.min(18, (width / days) * 0.55));
}

export function allocateEvenDollars(total: number, keys: string[]): Map<string, number> {
  const out = new Map<string, number>();
  if (keys.length === 0) return out;
  const n = keys.length;
  const base = Math.trunc(total / n);
  let rem = total - base * n;
  for (const key of keys) {
    let extra = 0;
    if (rem > 0) {
      extra = 1;
      rem -= 1;
    } else if (rem < 0) {
      extra = -1;
      rem += 1;
    }
    out.set(key, base + extra);
  }
  return out;
}

/** Remaining days in the current month; all days in a future month; none in the past. */
export function burnDayKeysForMonth(month: Date, today: Date): string[] {
  const keys = dayKeysInMonth(month);
  const todayKey = toDayKey(today);
  const monthKey = toMonthKey(new Date(month.getFullYear(), month.getMonth(), 1));
  const todayMonth = todayKey.slice(0, 7);
  if (monthKey > todayMonth) return keys;
  if (monthKey < todayMonth) return [];
  return keys.filter((key) => key >= todayKey);
}

/** Biweekly anchor when People → Benefits has no payroll schedule configured. */
export function defaultForecastPayrollSchedule(): PayrollSchedule {
  return { type: 'biweekly', anchorPayDate: '2026-10-30' };
}

export function resolveForecastPayrollSchedule(
  schedule: PayrollSchedule | null | undefined,
): PayrollSchedule {
  return parsePayrollSchedule(schedule) ?? defaultForecastPayrollSchedule();
}

/** Pay dates in a month that still receive forecast burn (today onward in the current month). */
export function payrollBurnDayKeysForMonth(
  month: Date,
  today: Date,
  schedule: PayrollSchedule,
): string[] {
  const burnKeys = burnDayKeysForMonth(month, today);
  if (burnKeys.length === 0) return [];
  const burnKeySet = new Set(burnKeys);
  const monthStart = new Date(month.getFullYear(), month.getMonth(), 1);
  const monthEnd = new Date(month.getFullYear(), month.getMonth() + 1, 0);
  const payDates = listPayDatesInRange(schedule, monthStart, monthEnd);
  const inWindow = payDates.filter((key) => burnKeySet.has(key));
  if (inWindow.length > 0) return inWindow;
  return burnKeys.slice(0, 1);
}

/** Split monthly burn into per-day overhead (even) and payroll (full payday lumps). */
export function buildForecastBurnByDayKey(
  months: CashFlowProjectionMonth[],
  today: Date,
  payrollSchedule: PayrollSchedule,
  options?: {
    /** When set, overhead dollars land on these day keys instead of even spread. */
    overheadByDayKey?: Map<string, number>;
  },
): Map<string, number> {
  const burnByDay = new Map<string, number>();
  const overheadByDay = options?.overheadByDayKey;
  for (const month of months) {
    // When cashFlowProjection already scaled payrollBurn by payday count,
    // divide across remaining paydays so each still gets a full check.
    // When unscaled (average monthly), payrollAmountPerPayday uses /typical.
    const payrollBurn = Math.max(0, Math.round(month.payrollBurn ?? 0));
    const overheadBurn = Math.max(0, Math.round(month.burn - payrollBurn));
    const payrollKeys = payrollBurn > 0
      ? payrollBurnDayKeysForMonth(month.month, today, payrollSchedule)
      : [];
    const perPayday = payrollKeys.length > 0
      ? Math.round(payrollBurn / payrollKeys.length)
      : payrollAmountPerPayday(payrollBurn, payrollSchedule);
    for (const key of payrollKeys) {
      burnByDay.set(key, (burnByDay.get(key) ?? 0) + perPayday);
    }

    const overheadKeys = burnDayKeysForMonth(month.month, today);
    const burnKeySet = new Set(overheadKeys);
    let datedOverhead = 0;
    if (overheadByDay && overheadByDay.size > 0) {
      const monthKey = `${month.month.getFullYear()}-${String(month.month.getMonth() + 1).padStart(2, '0')}`;
      overheadByDay.forEach((amount, key) => {
        if (!key.startsWith(monthKey)) return;
        if (amount <= 0) return;
        if (!burnKeySet.has(key)) return;
        const rounded = Math.round(amount);
        datedOverhead += rounded;
        burnByDay.set(key, (burnByDay.get(key) ?? 0) + rounded);
      });
    }
    // Budgeted OpEx (and any undated remainder) still spreads across remaining days.
    const undatedOverhead = Math.max(0, overheadBurn - datedOverhead);
    allocateEvenDollars(undatedOverhead, overheadKeys).forEach((amount, key) => {
      burnByDay.set(key, (burnByDay.get(key) ?? 0) + amount);
    });
  }
  return burnByDay;
}

function emptyDayBucket(dayKey: string, day: Date): ForecastDayBucket {
  return {
    dayKey,
    day,
    monthKey: dayKey.slice(0, 7),
    arIn: 0,
    apOut: 0,
    plannedOut: 0,
    jobOut: 0,
    items: [],
    quoteMarkers: [],
    hasEstimatedAr: false,
  };
}

export function buildForecastDayBuckets(
  months: CashFlowProjectionMonth[],
  today: Date,
  quoteMarkers: ForecastQuoteCoverageMarker[] = [],
): Map<string, ForecastDayBucket> {
  const buckets = new Map<string, ForecastDayBucket>();
  const ensure = (dayKey: string): ForecastDayBucket => {
    const existing = buckets.get(dayKey);
    if (existing) return existing;
    const day = parseDayKey(dayKey) ?? startOfLocalDay(today);
    const created = emptyDayBucket(dayKey, day);
    buckets.set(dayKey, created);
    return created;
  };

  for (const month of months) {
    for (const item of month.items) {
      const dayKey = cashFlowItemDayKey(item, today);
      const bucket = ensure(dayKey);
      bucket.items.push(item);
      if (item.kind === 'ar') {
        bucket.arIn += item.amount;
        if (item.estimated) bucket.hasEstimatedAr = true;
      } else if (item.kind === 'ap' && !item.withinBurnBudget) {
        bucket.apOut += item.amount;
      } else if (item.kind === 'planned') {
        bucket.plannedOut += item.amount;
      } else if (item.kind === 'job') {
        bucket.jobOut += item.amount;
      }
    }
  }

  for (const marker of quoteMarkers) {
    const dayKey = quoteMarkerDayKey(marker, today);
    ensure(dayKey).quoteMarkers.push(marker);
  }

  return buckets;
}

export function walkForecastDailyBalances(input: {
  months: CashFlowProjectionMonth[];
  startingBalance: number;
  today: Date;
  dayBuckets?: Map<string, ForecastDayBucket>;
  fromDay?: Date;
  toDay?: Date;
  payrollSchedule?: PayrollSchedule | null;
  overheadByDayKey?: Map<string, number>;
}): ForecastDailyBalancePoint[] {
  const { months, today } = input;
  const dayBuckets = input.dayBuckets ?? buildForecastDayBuckets(months, today);
  if (months.length === 0) return [];

  const firstMonth = months[0].month;
  const lastMonth = months[months.length - 1].month;
  const walkStart = new Date(firstMonth.getFullYear(), firstMonth.getMonth(), 1);
  const walkEnd = new Date(lastMonth.getFullYear(), lastMonth.getMonth() + 1, 0);
  const from = input.fromDay ? startOfLocalDay(input.fromDay) : walkStart;
  const to = input.toDay ? startOfLocalDay(input.toDay) : walkEnd;

  const schedule = resolveForecastPayrollSchedule(input.payrollSchedule);
  const burnByDay = buildForecastBurnByDayKey(months, today, schedule, {
    overheadByDayKey: input.overheadByDayKey,
  });

  const out: ForecastDailyBalancePoint[] = [];
  let balance = Math.round(input.startingBalance);
  for (const day of enumerateDays(walkStart, walkEnd)) {
    const dayKey = toDayKey(day);
    const bucket = dayBuckets.get(dayKey);
    const arIn = bucket?.arIn ?? 0;
    const apOut = bucket?.apOut ?? 0;
    const plannedOut = bucket?.plannedOut ?? 0;
    const jobOut = bucket?.jobOut ?? 0;
    const burn = burnByDay.get(dayKey) ?? 0;
    balance += arIn - apOut - burn - plannedOut - jobOut;
    if (day < from || day > to) continue;
    out.push({
      dayKey,
      day,
      monthKey: dayKey.slice(0, 7),
      projectedCash: balance,
      arIn,
      apOut,
      plannedOut,
      jobOut,
      burn,
      items: bucket?.items ?? [],
      quoteMarkers: bucket?.quoteMarkers ?? [],
      hasEstimatedAr: bucket?.hasEstimatedAr ?? false,
    });
  }
  return out;
}

/**
 * Roll a daily cash walk into Sunday–Saturday weeks. Totals match summing
 * the day's AR / AP / planned / job / burn; week-end cash is the last day.
 */
export function rollDailyWalkToWeeks(
  points: ForecastDailyBalancePoint[],
): ForecastWeeklyBalancePoint[] {
  const groups = new Map<string, ForecastWeeklyBalancePoint>();
  const order: string[] = [];
  for (const point of points) {
    const weekStart = startOfWeekSunday(point.day);
    const weekKey = toDayKey(weekStart);
    let group = groups.get(weekKey);
    if (!group) {
      group = {
        weekKey,
        weekStart,
        weekEnd: addLocalDays(weekStart, 6),
        monthKey: toMonthKey(weekStart),
        projectedCash: point.projectedCash,
        arIn: 0,
        apOut: 0,
        plannedOut: 0,
        jobOut: 0,
        burn: 0,
        items: [],
        quoteMarkers: [],
        hasEstimatedAr: false,
      };
      groups.set(weekKey, group);
      order.push(weekKey);
    }
    group.arIn += point.arIn;
    group.apOut += point.apOut;
    group.plannedOut += point.plannedOut;
    group.jobOut += point.jobOut;
    group.burn += point.burn;
    group.projectedCash = point.projectedCash;
    if (point.items.length) group.items = group.items.concat(point.items);
    if (point.quoteMarkers.length) {
      group.quoteMarkers = group.quoteMarkers.concat(point.quoteMarkers);
    }
    if (point.hasEstimatedAr) group.hasEstimatedAr = true;
  }
  return order.map((key) => groups.get(key)!);
}

export function sequenceCashByDayKey(
  report: Pick<DailyFinanceReport, 'history' | 'sequencePodAmount' | 'dateKey'> | null | undefined,
): Map<string, number> {
  const byDay = new Map<string, number>();
  if (!report) return byDay;
  for (const [dateKey, entry] of Object.entries(report.history || {})) {
    if (!parseDayKey(dateKey)) continue;
    if (typeof entry?.sequencePodAmount !== 'number' || !Number.isFinite(entry.sequencePodAmount)) continue;
    byDay.set(dateKey, entry.sequencePodAmount);
  }
  if (
    report.dateKey
    && parseDayKey(report.dateKey)
    && typeof report.sequencePodAmount === 'number'
    && Number.isFinite(report.sequencePodAmount)
  ) {
    byDay.set(report.dateKey, report.sequencePodAmount);
  }
  return byDay;
}

/**
 * Sequence already stores a cash balance for every past day. Map those
 * snapshots onto the month-index axis so Month/Week views can draw the
 * historical cash line daily instead of one point per month or week.
 */
export function historicalSequenceChartPoints(opts: {
  axisMonthKeys: string[];
  sequenceByDay: Map<string, number>;
  today: Date;
}): Array<{
  x: number;
  dayKey: string;
  monthKey: string;
  label: string;
  actualCash: number;
}> {
  const indexByKey = monthIndexByKey(opts.axisMonthKeys);
  const todayKey = toDayKey(opts.today);
  const out: Array<{
    x: number;
    dayKey: string;
    monthKey: string;
    label: string;
    actualCash: number;
  }> = [];
  opts.sequenceByDay.forEach((amount, dayKey) => {
    if (dayKey > todayKey) return;
    if (typeof amount !== 'number' || !Number.isFinite(amount)) return;
    const day = parseDayKey(dayKey);
    if (!day) return;
    const monthKey = toMonthKey(day);
    const monthIndex = indexByKey.get(monthKey);
    if (monthIndex == null) return;
    out.push({
      x: xForDay(day, monthIndex),
      dayKey,
      monthKey,
      label: formatForecastDayLabel(day),
      actualCash: Math.round(amount),
    });
  });
  out.sort((a, b) => a.x - b.x || a.dayKey.localeCompare(b.dayKey));
  return out;
}

function sign(n: number): number {
  if (n < 0) return -1;
  if (n > 0) return 1;
  return 0;
}

/**
 * Monotone cubic SVG path (same family as Recharts `type="monotone"`) through
 * points sorted by x. Used for projected cash — historic Sequence stays polyline.
 */
export function monotoneXSplinePathD(points: Array<{ x: number; y: number }>): string | null {
  const pts = points.filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y));
  if (pts.length < 2) return null;
  pts.sort((a, b) => a.x - b.x);
  const n = pts.length;
  if (n === 2) {
    return `M${pts[0].x} ${pts[0].y} L${pts[1].x} ${pts[1].y}`;
  }

  const dx: number[] = [];
  const dy: number[] = [];
  for (let i = 0; i < n - 1; i += 1) {
    dx.push(pts[i + 1].x - pts[i].x);
    dy.push(pts[i + 1].y - pts[i].y);
  }
  const m = dx.map((h, i) => dy[i] / h);

  const t: number[] = new Array(n).fill(0);
  t[0] = m[0];
  t[n - 1] = m[n - 2];
  for (let i = 1; i < n - 1; i += 1) {
    const m0 = m[i - 1];
    const m1 = m[i];
    if (m0 * m1 <= 0) {
      t[i] = 0;
    } else {
      const h0 = dx[i - 1];
      const h1 = dx[i];
      const p = (h0 + h1) / (h0 / m0 + h1 / m1);
      t[i] = (sign(m0) + sign(m1)) * Math.min(Math.abs(m0), Math.abs(m1), Math.abs(p) * 0.5);
    }
  }

  let d = `M${pts[0].x} ${pts[0].y}`;
  for (let i = 0; i < n - 1; i += 1) {
    const h = dx[i];
    const x0 = pts[i].x;
    const y0 = pts[i].y;
    const x1 = pts[i + 1].x;
    const y1 = pts[i + 1].y;
    const cp1x = x0 + h / 3;
    const cp1y = y0 + (t[i] * h) / 3;
    const cp2x = x1 - h / 3;
    const cp2y = y1 - (t[i + 1] * h) / 3;
    d += ` C${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${x1} ${y1}`;
  }
  return d;
}

/**
 * Month/Week bars stay on period ticks, but Now sits on today. Drop any
 * projected point at or before Now (the current-month/week tick) and join
 * the dashed forecast to today's cash.
 */
export function joinProjectedCashAtNow(opts: {
  nowX: number;
  nowCash: number | null;
  rows: Array<{ x: number; cash: number | null }>;
}): Array<{ x: number; y: number }> {
  const out: Array<{ x: number; y: number }> = [];
  if (typeof opts.nowCash === 'number' && Number.isFinite(opts.nowCash)) {
    out.push({ x: opts.nowX, y: opts.nowCash });
  }
  opts.rows.forEach((row) => {
    if (!Number.isFinite(row.x) || row.x <= opts.nowX) return;
    if (typeof row.cash !== 'number' || !Number.isFinite(row.cash)) return;
    out.push({ x: row.x, y: row.cash });
  });
  return out;
}

export function monthIndexByKey(monthKeys: string[]): Map<string, number> {
  const map = new Map<string, number>();
  monthKeys.forEach((key, index) => map.set(key, index));
  return map;
}

export type DailyForecastChartSource = {
  dayKey: string;
  day: Date;
  monthKey: string;
  x: number;
  label: string;
  actualCash: number | null;
  projectedCash: number | null;
  scenarioCash: number | null;
  arIn: number;
  apOut: number;
  plannedOut: number;
  jobOut: number;
  burn: number;
  hasEstimatedAr: boolean;
  quoteMarkers: ForecastQuoteCoverageMarker[];
  hasDetail: boolean;
};

function walkByDayKey(points: ForecastDailyBalancePoint[]): Map<string, ForecastDailyBalancePoint> {
  return new Map(points.map((point) => [point.dayKey, point]));
}

function formatDayLabel(day: Date): string {
  return formatForecastDayLabel(day);
}

function formatWeekLabel(weekStart: Date): string {
  return formatForecastWeekLabel(weekStart);
}

function walkByWeekKey(
  points: ForecastWeeklyBalancePoint[],
): Map<string, ForecastWeeklyBalancePoint> {
  return new Map(points.map((point) => [point.weekKey, point]));
}

function lastSequenceInWeek(
  weekStart: Date,
  weekEnd: Date,
  sequenceByDay: Map<string, number>,
): number | null {
  let last: number | null = null;
  for (const day of enumerateDays(weekStart, weekEnd)) {
    const value = sequenceByDay.get(toDayKey(day));
    if (value != null && Number.isFinite(value)) last = value;
  }
  return last;
}

function weekAxisPlacement(
  weekStart: Date,
  indexByKey: Map<string, number>,
): { monthIndex: number; xDay: Date } | null {
  const plotDay = weekPlotDay(weekStart);
  const plotIndex = indexByKey.get(toMonthKey(plotDay));
  if (plotIndex != null) return { monthIndex: plotIndex, xDay: plotDay };
  const startIndex = indexByKey.get(toMonthKey(weekStart));
  if (startIndex != null) return { monthIndex: startIndex, xDay: weekStart };
  return null;
}

export type WeeklyForecastChartSource = {
  weekKey: string;
  weekStart: Date;
  weekEnd: Date;
  monthKey: string;
  x: number;
  label: string;
  actualCash: number | null;
  projectedCash: number | null;
  scenarioCash: number | null;
  arIn: number;
  apOut: number;
  plannedOut: number;
  jobOut: number;
  burn: number;
  hasEstimatedAr: boolean;
  quoteMarkers: ForecastQuoteCoverageMarker[];
  hasDetail: boolean;
};

export function buildDailyForecastChartSources(opts: {
  axisMonthKeys: string[];
  monthStarts: Date[];
  fromDay: Date;
  toDay: Date;
  today: Date;
  displayWalk: ForecastDailyBalancePoint[];
  bakedWalk: ForecastDailyBalancePoint[];
  scenarioWalk?: ForecastDailyBalancePoint[] | null;
  sequenceByDay: Map<string, number>;
  scenarioActive?: boolean;
}): DailyForecastChartSource[] {
  const indexByKey = monthIndexByKey(opts.axisMonthKeys);
  const displayByDay = walkByDayKey(opts.displayWalk);
  const bakedByDay = walkByDayKey(opts.bakedWalk);
  const scenarioByDay = opts.scenarioWalk ? walkByDayKey(opts.scenarioWalk) : null;
  const todayKey = toDayKey(opts.today);
  const out: DailyForecastChartSource[] = [];

  for (const day of enumerateDays(opts.fromDay, opts.toDay)) {
    const monthKey = toMonthKey(day);
    const monthIndex = indexByKey.get(monthKey);
    if (monthIndex == null) continue;
    const dayKey = toDayKey(day);
    const display = displayByDay.get(dayKey);
    const baked = bakedByDay.get(dayKey);
    const scenario = scenarioByDay?.get(dayKey);
    const actual = opts.sequenceByDay.get(dayKey);
    const hasDetail = Boolean(
      display
      && (
        display.arIn > 0
        || display.apOut > 0
        || display.plannedOut > 0
        || display.jobOut > 0
        || display.quoteMarkers.length > 0
      ),
    );
    const isPast = dayKey < todayKey;
    out.push({
      dayKey,
      day,
      monthKey,
      x: xForDay(day, monthIndex),
      label: formatDayLabel(day),
      actualCash: actual != null && Number.isFinite(actual) ? Math.round(actual) : null,
      // Don't paint a flat projected line across days that already happened —
      // actual Sequence cash owns the past; the dashed line starts at today.
      projectedCash: !isPast && baked ? baked.projectedCash : null,
      scenarioCash: !isPast && opts.scenarioActive && scenario ? scenario.projectedCash : null,
      arIn: display?.arIn ?? 0,
      apOut: display?.apOut ?? 0,
      plannedOut: display?.plannedOut ?? 0,
      jobOut: display?.jobOut ?? 0,
      burn: display?.burn ?? 0,
      hasEstimatedAr: display?.hasEstimatedAr ?? false,
      quoteMarkers: display?.quoteMarkers ?? [],
      hasDetail,
    });
  }
  return out;
}

export function buildWeeklyForecastChartSources(opts: {
  axisMonthKeys: string[];
  monthStarts: Date[];
  fromDay: Date;
  toDay: Date;
  today: Date;
  displayWalk: ForecastWeeklyBalancePoint[];
  bakedWalk: ForecastWeeklyBalancePoint[];
  scenarioWalk?: ForecastWeeklyBalancePoint[] | null;
  sequenceByDay: Map<string, number>;
  scenarioActive?: boolean;
}): WeeklyForecastChartSource[] {
  const indexByKey = monthIndexByKey(opts.axisMonthKeys);
  const displayByWeek = walkByWeekKey(opts.displayWalk);
  const bakedByWeek = walkByWeekKey(opts.bakedWalk);
  const scenarioByWeek = opts.scenarioWalk ? walkByWeekKey(opts.scenarioWalk) : null;
  const todayKey = toDayKey(opts.today);
  const out: WeeklyForecastChartSource[] = [];

  for (const weekStart of enumerateWeeks(opts.fromDay, opts.toDay)) {
    const placement = weekAxisPlacement(weekStart, indexByKey);
    if (!placement) continue;
    const weekKey = toDayKey(weekStart);
    const weekEnd = addLocalDays(weekStart, 6);
    const display = displayByWeek.get(weekKey);
    const baked = bakedByWeek.get(weekKey);
    const scenario = scenarioByWeek?.get(weekKey);
    const actual = lastSequenceInWeek(weekStart, weekEnd, opts.sequenceByDay);
    const hasDetail = Boolean(
      display
      && (
        display.arIn > 0
        || display.apOut > 0
        || display.plannedOut > 0
        || display.jobOut > 0
        || display.burn > 0
        || display.quoteMarkers.length > 0
      ),
    );
    const isPast = toDayKey(weekEnd) < todayKey;
    out.push({
      weekKey,
      weekStart,
      weekEnd,
      monthKey: toMonthKey(placement.xDay),
      x: xForDay(placement.xDay, placement.monthIndex),
      label: formatWeekLabel(weekStart),
      actualCash: actual != null ? Math.round(actual) : null,
      projectedCash: !isPast && baked ? baked.projectedCash : null,
      scenarioCash: !isPast && opts.scenarioActive && scenario ? scenario.projectedCash : null,
      arIn: display?.arIn ?? 0,
      apOut: display?.apOut ?? 0,
      plannedOut: display?.plannedOut ?? 0,
      jobOut: display?.jobOut ?? 0,
      burn: display?.burn ?? 0,
      hasEstimatedAr: display?.hasEstimatedAr ?? false,
      quoteMarkers: display?.quoteMarkers ?? [],
      hasDetail,
    });
  }
  return out;
}

export function zoomWindowToDayRange(
  zoomWindow: { start: number; end: number } | null,
  nMonths: number,
  monthStarts: Date[],
  padDays: number = FORECAST_DAILY_PAD_DAYS,
): { fromDay: Date; toDay: Date } | null {
  if (!monthStarts.length || nMonths <= 0) return null;
  const lo = zoomWindow ? zoomWindow.start * nMonths - 0.5 : -0.5;
  const hi = zoomWindow ? zoomWindow.end * nMonths - 0.5 : nMonths - 0.5;
  const start = dateFromMonthX(Math.max(0, lo), monthStarts);
  const end = dateFromMonthX(Math.min(nMonths - 1e-9, hi), monthStarts);
  if (!start || !end) return null;
  const lastMonth = monthStarts[monthStarts.length - 1];
  const axisEnd = new Date(lastMonth.getFullYear(), lastMonth.getMonth() + 1, 0);
  const fromDay = addLocalDays(start, -padDays);
  const toDay = addLocalDays(end, padDays);
  const axisStart = startOfLocalDay(monthStarts[0]);
  return {
    fromDay: fromDay < axisStart ? axisStart : fromDay,
    toDay: toDay > axisEnd ? axisEnd : toDay,
  };
}

/**
 * How far below zero the forecast axis should reach.
 * Twenty percent past the lowest value, so the line is not pinned to the floor.
 * Null when the series never goes negative — the chart keeps a zero baseline.
 */
export function forecastNegativeAxisFloor(lowest: number): number | null {
  if (!Number.isFinite(lowest) || !(lowest < 0)) return null;
  return lowest - Math.abs(lowest) * 0.2;
}

/**
 * Tick labels once the axis extends below zero.
 * One label sits on the padded floor; zero and the positive steps stay.
 * Undefined when the domain is non-negative so Recharts can pick ticks itself.
 */
export function forecastYAxisTicks(domainMin: number, domainMax: number): number[] | undefined {
  if (!(domainMin < 0) || !Number.isFinite(domainMin) || !Number.isFinite(domainMax) || !(domainMax > domainMin)) {
    return undefined;
  }
  const rough = Math.max(domainMax, 1) / 4;
  const pow = 10 ** Math.floor(Math.log10(Math.max(rough, 1)));
  const fraction = rough / pow;
  const nice = fraction >= 7.5 ? 10 : fraction >= 3.5 ? 5 : fraction >= 1.5 ? 2 : 1;
  const step = nice * pow;
  const values = [domainMin, 0];
  const count = Math.floor((domainMax - step * 0.02) / step);
  for (let i = 1; i <= count; i += 1) {
    const v = i * step;
    if (v > 0 && v < domainMax) values.push(v);
  }
  const minGap = (domainMax - domainMin) * 0.06;
  const kept: number[] = [];
  for (const t of values) {
    const anchor = t === domainMin || t === 0;
    if (!anchor && kept.some((k) => Math.abs(k - t) < minGap)) continue;
    kept.push(t);
  }
  kept.sort((a, b) => a - b);
  return kept;
}
